import React from 'react';
import { Edit, Calendar, FileText, Phone, Mail, Power, PowerOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import { SortableHeader } from '@/components/ui/sortable-header';
import { useTableData } from '@/hooks/useTableData';
import { getLicenseTypeLabel } from '../mocks/system-parameters.mock';
import type { Driver } from '../types/system-parameters.types';

interface DriversTableProps {
  drivers: Driver[];
  onEdit: (driver: Driver) => void;
  onDelete: (driver: Driver) => void;
}

type SortField = 'fullName' | 'licenseNumber' | 'licenseType' | 'licenseExpiry';

const isExpiringSoon = (dateString: string): boolean => {
  const expiryDate = new Date(dateString);
  const today = new Date();
  const diffTime = expiryDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays <= 30 && diffDays > 0;
};

const isExpired = (dateString: string): boolean => {
  const expiryDate = new Date(dateString);
  const today = new Date();
  return expiryDate < today;
};

export const DriversTable: React.FC<DriversTableProps> = ({
  drivers,
  onEdit,
  onDelete
}) => {
  const fieldLabels: Record<SortField, string> = {
    fullName: 'Conductor',
    licenseNumber: 'Licencia',
    licenseType: 'Tipo',
    licenseExpiry: 'Vencimiento'
  };

  const sortValueExtractor = (driver: Driver, field: SortField): any => {
    switch (field) {
      case 'fullName':
        return driver.additionalData.fullName.toLowerCase();
      case 'licenseNumber':
        return driver.additionalData.licenseNumber.toLowerCase();
      case 'licenseType':
        return driver.additionalData.licenseType;
      case 'licenseExpiry':
        return new Date(driver.additionalData.licenseExpiry);
      default:
        return driver.additionalData.fullName;
    }
  };

  const tableData = useTableData<Driver, SortField>(
    {
      data: drivers,
      defaultSortField: 'fullName',
      defaultSortDirection: 'asc',
      itemsPerPage: 10,
      sortValueExtractor
    },
    fieldLabels
  );

  if (drivers.length === 0) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">No hay conductores</h3>
        <p className="text-gray-500">No se encontraron conductores con los filtros aplicados.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <SortableHeader
                field="fullName"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                Conductor
              </SortableHeader>
              <SortableHeader
                field="licenseNumber"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                Licencia
              </SortableHeader>
              <SortableHeader
                field="licenseType"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-center"
              >
                Tipo
              </SortableHeader>
              <SortableHeader
                field="licenseExpiry"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-center"
              >
                Vencimiento
              </SortableHeader>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contacto
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tableData.pagination.paginatedData.map((driver) => {
              const isLicenseExpiring = isExpiringSoon(driver.additionalData.licenseExpiry);
              const isLicenseExpired = isExpired(driver.additionalData.licenseExpiry);

              return (
                <tr key={driver.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {driver.additionalData.fullName}
                      </div>
                      <div className="text-xs text-gray-400">
                        ID: {driver.id}
                      </div>
                    </div>
                  </td>

                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2 text-sm">
                      <FileText className="h-4 w-4 text-gray-500" />
                      <span className="font-mono">{driver.additionalData.licenseNumber}</span>
                    </div>
                  </td>

                  <td className="px-4 py-4 text-center">
                    <Badge variant="outline">
                      {getLicenseTypeLabel(driver.additionalData.licenseType)}
                    </Badge>
                  </td>

                  <td className="px-4 py-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-500" />
                      <span className={`text-sm ${isLicenseExpired ? 'text-red-600 font-semibold' : isLicenseExpiring ? 'text-orange-600 font-semibold' : 'text-gray-900'}`}>
                        {new Date(driver.additionalData.licenseExpiry).toLocaleDateString('es-PE')}
                      </span>
                      {isLicenseExpired && <Badge variant="destructive" className="text-xs">Vencida</Badge>}
                      {isLicenseExpiring && !isLicenseExpired && <Badge variant="outline" className="text-xs text-orange-600 border-orange-600">Por vencer</Badge>}
                    </div>
                  </td>

                  <td className="px-4 py-4">
                    <div className="text-sm space-y-1">
                      {driver.additionalData.phone && (
                        <div className="flex items-center gap-1 text-gray-600">
                          <Phone className="h-3 w-3" />
                          {driver.additionalData.phone}
                        </div>
                      )}
                      {driver.additionalData.email && (
                        <div className="flex items-center gap-1 text-blue-600">
                          <Mail className="h-3 w-3" />
                          {driver.additionalData.email}
                        </div>
                      )}
                    </div>
                  </td>

                  <td className="px-4 py-4 text-center">
                    <Badge className={driver.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                      {driver.isActive ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </td>

                  <td className="px-4 py-4 text-center">
                    <div className="flex gap-2 justify-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEdit(driver)}
                        className="text-xs"
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Editar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onDelete(driver)}
                        className={`text-xs ${driver.additionalData?.isActive
                          ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50'
                          : 'text-green-600 hover:text-green-700 hover:bg-green-50'}`}
                      >
                        {driver.additionalData?.isActive ? (
                          <><PowerOff className="w-3 h-3 mr-1" />Desactivar</>
                        ) : (
                          <><Power className="w-3 h-3 mr-1" />Reactivar</>
                        )}
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Sort info */}
      <div className="mt-2 text-sm text-gray-500">
        Ordenado por: <span className="font-medium">
          {tableData.sortInfo.label}
        </span> ({tableData.sortInfo.direction === 'asc' ? 'Ascendente' : 'Descendente'})
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={tableData.pagination.currentPage}
        totalPages={tableData.pagination.totalPages}
        onPageChange={tableData.pagination.goToPage}
        showingFrom={tableData.pagination.showingFrom}
        showingTo={tableData.pagination.showingTo}
        totalItems={tableData.pagination.totalItems}
        itemsPerPage={tableData.pagination.itemsPerPage}
        onItemsPerPageChange={tableData.pagination.setItemsPerPage}
      />
    </div>
  );
};
